import { useEffect, useState } from "react";
import { Button, Input, DatePicker, Card, Spin, Modal, Row, Col } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEagleEyeAuth } from "hooks/useEagleEyeAuth";
import { useEagleEyeApi, EenFullFrameImageData, EenSearchVideoResponse } from "hooks/useEagleEyeApi";
import { formatEenTimestamp } from "utils/helpers";
import { HiSearch } from "react-icons/hi";

const DEFAULT_TIME_RANGE: [Dayjs, Dayjs] = [
  dayjs().subtract(1, "day").startOf("day"),
  dayjs().endOf("day"),
];

const EagleEyeVideoSearch: React.FC = () => {
  const {
    isAuthenticated,
    isAuthenticating,
    authError,
    loginInPopup,
    logout,
  } = useEagleEyeAuth();
  // Auth failures are handled inside the hook: clearAuthData() sets isAuthenticated → false,
  // causing the component to render the login screen automatically.
  const {
    searchVideo,
    fetchVideoThumbnail,
    isLoading,
    error: apiError
  } = useEagleEyeApi();

  // Close the popup window once authenticated (used by the loginInPopup flow)
  useEffect(() => {
    if (isAuthenticated && window.opener) {
      window.close();
    }
  }, [isAuthenticated]);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [videoTimeRange, setVideoTimeRange] = useState<[Dayjs, Dayjs]>(DEFAULT_TIME_RANGE);
  const [thumbnailUrls, setThumbnailUrls] = useState<{ imgUrl: string; deeplink: string | undefined }[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [selectedThumbnail, setSelectedThumbnail] = useState<{ imgUrl: string; deeplink: string | undefined } | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  // Revoke blob URLs when thumbnails are replaced or component unmounts
  useEffect(() => {
    return () => {
      thumbnailUrls.forEach(({ imgUrl }) => {
        if (imgUrl.startsWith("blob:")) URL.revokeObjectURL(imgUrl);
      });
    };
  }, [thumbnailUrls]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setThumbnailUrls([]);
    setTotalResults(0);
    setHasSearched(true);

    const response = await searchVideo(searchQuery, undefined, {
      start: videoTimeRange[0].toISOString().replace("Z", "+00:00"),
      end: videoTimeRange[1].toISOString().replace("Z", "+00:00"),
    }) as EenSearchVideoResponse | null;

    if (response) {
      const results = response.results ?? [];
      setTotalResults(results.length);

      const candidates = results
        .slice(0, 10)
        .map((result: EenSearchVideoResponse["results"][number]) => {
          const imageData = result.data?.find(
            (d: EenSearchVideoResponse["results"][number]["data"][number]): d is EenFullFrameImageData =>
              d.type === "een.fullFrameImageUrl.v1"
          );
          if (!imageData) return null;
          const esn = result.actorType === "camera" ? result.actorId : undefined;
          const deeplink = esn
            ? `https://webapp.eagleeyenetworks.com/#/history?ids=${esn}&time=${formatEenTimestamp(imageData.timestamp)}`
            : undefined;
          return { httpsUrl: imageData.httpsUrl, deeplink };
        })
        .filter((item): item is { httpsUrl: string; deeplink: string | undefined } => item !== null);

      const thumbnails = await Promise.all(
        candidates.map(async ({ httpsUrl, deeplink }) => ({
          imgUrl: await fetchVideoThumbnail(httpsUrl),
          deeplink,
        }))
      );
      setThumbnailUrls(thumbnails);
    }
  };

  if (isAuthenticating) {
    return (
      <div className="ee-container">
        <span className="page-title">Video Search</span>
        <div className="ee-content-container">
          <Card className="ee-loading-card">
            <Spin size="large" />
            <h2>Authenticating with Eagle Eye Network...</h2>
          </Card>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="ee-container">
        <span className="page-title">Video Search</span>
        <div className="ee-content-container">
          <Card className="ee-error-card">
            <h2>Authentication Error</h2>
            <p>{authError}</p>
            <Button className="ee-retry-button" onClick={loginInPopup}>
              Try Again
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="ee-container">
        <span className="page-title">Video Search</span>
        <div className="ee-content-container">
          <Card className="ee-login-card">
            <h2>Eagle Eye Network Integration</h2>
            <p className="ee-description">
              Please authenticate to access Eagle Eye video search
            </p>
            <Button className="ee-login-button" onClick={loginInPopup}>
              Login with Eagle Eye Network
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // ── Authenticated UI ──────────────────────────────────────────────────────

  return (
    <div className="ee-container">
      <span className="page-title">Video Search</span>

      <div className="products-content-container">
        <div className="products-filter-group">
          <div className="search-products-row">
            <div className="products-search-container">
              <Input
                className="product-search-input-wrapper"
                classNames={{ input: "product-search-input" }}
                name="searchVideo"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='e.g. "blue truck at entry gate"'
                onPressEnter={handleSearch}
                prefix={<HiSearch color={"#67748E"} size={"16px"} />}
                autoComplete="off"
                allowClear
              />
            </div>
          </div>
          <div className="products-filters-and-create-guide-row">
            <div className="products-filters-row">
              <DatePicker.RangePicker
                className="ee-input"
                showTime
                value={videoTimeRange}
                onChange={(values) => {
                  if (values?.[0] && values?.[1]) {
                    setVideoTimeRange([values[0], values[1]]);
                  }
                }}
              />
            </div>
            <Button className="highnote-button" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>

        <div className="products-table">
          <img className="eevs-hr" src={`${process.env.PUBLIC_URL}/assets/images/icons/hr.svg`} />

          {thumbnailUrls.length > 0 && (
            <div className="eevs-results-title">
              Results
            </div>
          )}
          <Row className="products-grid-container" align="stretch" gutter={[24, 24]}>
            {thumbnailUrls.map(({ imgUrl, deeplink }, i) => (
              <Col key={imgUrl} span={4}>
                <img
                  src={imgUrl}
                  alt={`Video search result ${i + 1}`}
                  style={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                    cursor: "pointer",
                    borderRadius: 6,
                    display: "block",
                  }}
                  onClick={() => setSelectedThumbnail({ imgUrl, deeplink })}
                />
              </Col>
            ))}
            <img className="eevs-hr" src={`${process.env.PUBLIC_URL}/assets/images/icons/hr.svg`} />
          </Row>

          {apiError && (
            <Card className="ee-error-card">
              <h3>Error</h3>
              <p>{apiError}</p>
            </Card>
          )}

          {hasSearched && !isLoading && thumbnailUrls.length === 0 && !apiError && (
            <Card className="ee-response-card">
              <p style={{ textAlign: "center", color: "#67748e", margin: 0 }}>
                No results found. Try a different query or date range.
              </p>
            </Card>
          )}

          <Modal
            className="ee-img-preview-modal"
            open={selectedThumbnail !== null}
            onCancel={() => setSelectedThumbnail(null)}
            footer={null}
            centered
          >
            {selectedThumbnail && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  paddingTop: 8,
                }}
              >
                <img
                  src={selectedThumbnail.imgUrl}
                  alt="Video search result"
                  style={{ width: "100%", objectFit: "contain" }}
                />
                {selectedThumbnail.deeplink && (
                  <a
                    className="highnote-button"
                    href={selectedThumbnail.deeplink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Playback
                  </a>
                )}
              </div>
            )}
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default EagleEyeVideoSearch;
